import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "../lib/api";
import type { PlaceSearchResponse, PlaceSuggestion } from "@family/shared";

interface Props {
  /** Free-text label currently shown in the input. Two-way bound. */
  value: string;
  onChange: (text: string) => void;
  /** Called when the user picks a suggestion. Soft-fallback design:
   *  null clears the picked place and reverts to free text. */
  onPick: (place: PlaceSuggestion | null) => void;
  /** Hidden cue: when the field has a previously-picked place coords,
   *  we show a green checkmark. */
  hasPickedPlace: boolean;
  placeholder?: string;
  disabled?: boolean;
}

const DEBOUNCE_MS = 350;

/**
 * Single-input typeahead for the user's location. Calls our /me/places/search
 * proxy in front of Nominatim. Soft fallback: typing without picking is fine,
 * the field just stores the raw text and the World map falls back to its
 * cached city geocoder.
 */
export function PlaceTypeahead({
  value,
  onChange,
  onPick,
  hasPickedPlace,
  placeholder = "City, country (start typing)",
  disabled,
}: Props) {
  const listboxId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [debounced, setDebounced] = useState(value);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  // Suspends the query for one cycle right after a user picks a suggestion,
  // so the dropdown doesn't immediately re-open with results matching the
  // newly-set text.
  const [suppressQueryUntil, setSuppressQueryUntil] = useState(0);

  // Debounce the visible value into a separate state used as the query key.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [value]);

  const queryEnabled =
    open &&
    debounced.trim().length >= 2 &&
    Date.now() >= suppressQueryUntil;

  const search = useQuery<PlaceSearchResponse>({
    queryKey: ["place-search", debounced],
    queryFn: () =>
      apiFetch<PlaceSearchResponse>(
        `/me/places/search?q=${encodeURIComponent(debounced)}`,
      ),
    enabled: queryEnabled,
    staleTime: 60 * 60 * 1000, // 1h: results don't change much
    gcTime: 60 * 60 * 1000,
  });

  const results = useMemo(() => search.data?.results ?? [], [search.data]);

  function pickSuggestion(s: PlaceSuggestion) {
    onChange(s.displayName);
    onPick(s);
    setOpen(false);
    setActiveIdx(-1);
    setSuppressQueryUntil(Date.now() + DEBOUNCE_MS + 100);
    inputRef.current?.blur();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (activeIdx >= 0 && results[activeIdx]) {
        e.preventDefault();
        pickSuggestion(results[activeIdx]);
      }
    }
  }

  return (
    <div style={{ position: "relative" }}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          // Typing invalidates a previously-picked place — soft fallback
          // hands back to free-text geocoding.
          if (hasPickedPlace) onPick(null);
          setOpen(true);
          setActiveIdx(-1);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          // Delay so click on a suggestion still registers.
          setTimeout(() => setOpen(false), 150);
        }}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        maxLength={500}
        disabled={disabled}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={
          activeIdx >= 0 ? `${listboxId}-${activeIdx}` : undefined
        }
        style={{
          width: "100%",
          paddingRight: hasPickedPlace ? 36 : undefined,
        }}
      />
      {hasPickedPlace && (
        <span
          aria-label="Place selected"
          title="Place selected"
          style={{
            position: "absolute",
            right: 12,
            top: "50%",
            transform: "translateY(-50%)",
            color: "var(--c-success, #16a34a)",
            fontSize: "1.1rem",
            pointerEvents: "none",
          }}
        >
          ✓
        </span>
      )}

      {open && results.length > 0 && (
        <ul
          id={listboxId}
          role="listbox"
          className="list-unstyled"
          style={{
            position: "absolute",
            zIndex: 20,
            top: "100%",
            left: 0,
            right: 0,
            marginTop: 4,
            background: "var(--c-bg)",
            border: "1px solid var(--c-border-strong)",
            borderRadius: 8,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            maxHeight: 280,
            overflowY: "auto",
          }}
        >
          {results.map((r, i) => (
            <li
              key={r.id}
              id={`${listboxId}-${i}`}
              role="option"
              aria-selected={i === activeIdx}
            >
              <button
                type="button"
                onMouseDown={(e) => {
                  // Use mouseDown so the input blur fires after the pick.
                  e.preventDefault();
                  pickSuggestion(r);
                }}
                onMouseEnter={() => setActiveIdx(i)}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: "10px 14px",
                  background:
                    i === activeIdx
                      ? "var(--c-bg-alt, rgba(0,0,0,0.04))"
                      : "transparent",
                  border: "none",
                  borderBottom:
                    i < results.length - 1
                      ? "1px solid var(--c-border)"
                      : "none",
                  fontSize: "0.9rem",
                  color: "var(--c-text)",
                  cursor: "pointer",
                  lineHeight: 1.3,
                }}
              >
                <div style={{ fontWeight: 600 }}>
                  {[r.city, r.region, r.country].filter(Boolean).join(", ") ||
                    r.displayName}
                </div>
                {r.displayName && (
                  <div
                    className="text-subtle"
                    style={{
                      fontSize: "0.75rem",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {r.displayName}
                  </div>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      <p
        className="text-subtle"
        style={{ fontSize: "0.75rem", marginTop: 4 }}
      >
        Place search powered by{" "}
        <a
          href="https://www.openstreetmap.org/copyright"
          target="_blank"
          rel="noopener noreferrer"
        >
          OpenStreetMap
        </a>
      </p>
    </div>
  );
}
